import { StrictMode } from 'react'
import { hydrateRoot } from 'react-dom/client'
import * as devalue from 'devalue'
import App from './App'
import i18n, { DEFAULT_LANGUAGE } from './i18n'
import { DehydratedState } from '@tanstack/react-query'
import { I18nextProvider } from 'react-i18next'

export class BookingModule extends HTMLElement {
  readonly #shadowRoot: ShadowRoot
  readonly #appRoot: HTMLElement
  readonly #styleMap: Map<string, HTMLStyleElement>
  readonly #i18n: typeof i18n

  constructor () {
    super()
    this.#styleMap = new Map<string, HTMLStyleElement>()
    this.#shadowRoot = this.attachShadow({ mode: 'closed' })
    this.#appRoot = document.createElement('div')
    this.#appRoot.className = 'root'
    this.#shadowRoot.appendChild(this.#appRoot)
    this.#appRoot.innerHTML = this.querySelector('.root')?.innerHTML ?? ''
    const lang = this.getAttribute('lang') ?? DEFAULT_LANGUAGE
    this.#i18n = i18n.cloneInstance({
      lng: lang
    })
    i18n.changeLanguage(this.getAttribute('lang') ?? DEFAULT_LANGUAGE).catch(e => console.error('Error setting language', e))
    document.querySelectorAll('link.booking-module-css').forEach(link => {
      const clone = link.cloneNode(true) as HTMLLinkElement
      if (clone.tagName.toLowerCase() === 'link') {
        clone.rel = 'stylesheet'
      }
      this.#shadowRoot.appendChild(clone)
    })
    const updateStyleTag = (style: HTMLStyleElement): void => {
      const cloned = this.#styleMap.get(style.dataset.viteDevId ?? '')
      if (cloned != null) {
        cloned.innerHTML = style.innerHTML
      }
    }
    const observer = new MutationObserver((list) => {
      for (const mutation of list) {
        if (mutation.target.nodeType === Node.TEXT_NODE) {
          const parent = mutation.target.parentElement
          if (parent != null && parent.tagName.toLowerCase() === 'style') {
            const typedParent = parent as HTMLStyleElement
            updateStyleTag(typedParent)
          }
        }
      }
    })
    document.querySelectorAll('style[data-vite-dev-id]').forEach((style) => {
      const typedStyle = style as HTMLStyleElement
      const clone = typedStyle.cloneNode(true) as HTMLStyleElement
      this.#shadowRoot.appendChild(clone)
      this.#styleMap.set(typedStyle.dataset.viteDevId ?? '', clone)
      observer.observe(style, { characterData: true, characterDataOldValue: true, childList: true, subtree: true })
    })
    document.querySelectorAll('style.booking-module-css').forEach(style => {
      const clone = style.cloneNode(true) as HTMLStyleElement
      const content = clone.innerHTML.trim()
      if (content.startsWith('@scope{')) {
        clone.innerHTML = content.substring('@scope{'.length, content.length - 1)
      }
      this.#shadowRoot.appendChild(clone)
    })
  }

  get #state (): DehydratedState | null {
    const script = this.querySelector('script.state')
    if (script != null) {
      const serializedState = script.innerHTML
      return devalue.parse(serializedState)
    }
    return null
  }

  connectedCallback (): void {
    const tourId = this.getAttribute('tour')
    const shipId = this.getAttribute('ship')
    const year = this.getAttribute('year')
    hydrateRoot(
      this.#appRoot,
      <StrictMode>
        <I18nextProvider i18n={this.#i18n}>
          <App
            tourId={tourId} shipId={shipId} year={year != null ? parseInt(year, 10) : null}
            dehydratedState={this.#state}
          />
        </I18nextProvider>
      </StrictMode>,
      {
        onRecoverableError (error, errorInfo) {
          console.error('Hydration error:', error)
          console.error('Component stack:', errorInfo.componentStack)
        }
      }
    )
  }
}
